import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { PanResponder, StyleSheet, View, } from 'react-native';
import { NavigationContext } from '@react-navigation/native';
import useColors from 'hooks/useColors';
import useStyles from 'hooks/useStyles';
import createStyleSheets from 'utils/createStyleSheets';
import { rem } from './values';
const ACCESSIBILITY_ACTIONS = [
    { name: 'increment' },
    { name: 'decrement' },
];
const clamp = (n, min, max) => Math.min(Math.max(n, min), max);
const decimalsOf = (n) => {
    const [, decimals = ''] = String(n).split('.');
    return decimals.length;
};
const snapToStep = (raw, min, step) => step > 0
    ? Number((min + Math.round((raw - min) / step) * step).toFixed(decimalsOf(step)))
    : raw;
/**
 * A slider driven by a JS PanResponder rather than a worklet, so every value
 * reaches React state on the JS thread and text rendered from it never lags
 * behind the thumb. It also holds on to the gesture when a parent ScrollView
 * tries to take over mid-drag.
 */
const Slider = ({ value, minimumValue = 0, maximumValue = 1, maximumSelectableValue = maximumValue, step = 0, onValueChange, onSlidingStart, onSlidingComplete, error, minimumTrackTintColor, maximumTrackTintColor, containerStyle, trackStyle, thumbStyle, renderThumbComponent, width, thumbSize, testID, accessibilityLabel, accessibilityValueText, accessibilityIncrement, }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    // Stack screens let swipe back start anywhere on iOS (fullScreenGestureEnabled),
    // so dragging the thumb sideways popped the screen instead of moving the thumb.
    // On a screen with a slider, swipe back starts only from the left edge.
    const navigation = useContext(NavigationContext);
    useEffect(() => {
        navigation?.setOptions({ fullScreenGestureEnabled: false });
    }, [navigation]);
    const styledThumbWidth = StyleSheet.flatten([styles.thumb, thumbStyle]).width;
    const thumbWidth = thumbSize ??
        (!renderThumbComponent && typeof styledThumbWidth === 'number'
            ? styledThumbWidth
            : 0);
    // The thumb follows the finger without waiting for the parent, and resyncs
    // whenever the parent passes a new value.
    const [internalValue, setInternalValue] = useState(value);
    const [prevValueProp, setPrevValueProp] = useState(value);
    if (value !== prevValueProp) {
        setPrevValueProp(value);
        setInternalValue(value);
    }
    const range = maximumValue - minimumValue;
    const upperBound = clamp(maximumSelectableValue, minimumValue, maximumValue);
    const usableWidth = Math.max(width - thumbWidth, 0);
    const ratio = range > 0
        ? (clamp(internalValue, minimumValue, maximumValue) - minimumValue) /
            range
        : 0;
    const thumbLeft = ratio * usableWidth;
    // The PanResponder is created once, so it reads the latest values via a ref.
    const latest = useRef({
        internalValue,
        minimumValue,
        range,
        upperBound,
        usableWidth,
        thumbWidth,
        step,
        onValueChange,
        onSlidingStart,
        onSlidingComplete,
    });
    latest.current = {
        internalValue,
        minimumValue,
        range,
        upperBound,
        usableWidth,
        thumbWidth,
        step,
        onValueChange,
        onSlidingStart,
        onSlidingComplete,
    };
    const gesture = useRef({ startX: 0, grabOffset: 0, current: value });
    const valueForX = useCallback((x) => {
        const { usableWidth: w, thumbWidth: tw, minimumValue: min, range: r, upperBound: max, step: s, } = latest.current;
        const raw = min + clamp((x - tw / 2) / w, 0, 1) * r;
        return clamp(snapToStep(raw, min, s), min, max);
    }, []);
    const updateForX = useCallback((x) => {
        if (latest.current.usableWidth <= 0) {
            return;
        }
        const next = valueForX(x);
        if (next !== gesture.current.current) {
            gesture.current.current = next;
            setInternalValue(next);
            latest.current.onValueChange?.(next);
        }
    }, [valueForX]);
    const panResponder = useMemo(() => PanResponder.create({
        onStartShouldSetPanResponder: () => true,
        onMoveShouldSetPanResponder: () => true,
        // Keep the gesture when the parent ScrollView asks for it mid-drag.
        onPanResponderTerminationRequest: () => false,
        onShouldBlockNativeResponder: () => true,
        onPanResponderGrant: (evt) => {
            const { locationX } = evt.nativeEvent;
            const { internalValue: v, minimumValue: min, range: r, usableWidth: w, thumbWidth: tw, } = latest.current;
            const thumbCenter = (r > 0 ? (v - min) / r : 0) * w + tw / 2;
            // Touching the thumb grabs it where it is, so small adjustments are
            // not lost to a jump. Touching elsewhere on the track jumps there.
            const grabbedThumb = Math.abs(locationX - thumbCenter) <= tw;
            gesture.current = {
                startX: locationX,
                grabOffset: grabbedThumb ? thumbCenter - locationX : 0,
                current: v,
            };
            latest.current.onSlidingStart?.(v);
            if (!grabbedThumb) {
                updateForX(locationX);
            }
        },
        onPanResponderMove: (_evt, { dx }) => {
            updateForX(gesture.current.startX + gesture.current.grabOffset + dx);
        },
        onPanResponderRelease: () => {
            latest.current.onSlidingComplete?.(gesture.current.current);
        },
        onPanResponderTerminate: () => {
            latest.current.onSlidingComplete?.(gesture.current.current);
        },
    }), [updateForX]);
    // Screen readers adjust the value with swipes rather than a drag, so each
    // action runs the full start/change/complete sequence a drag would.
    const onAccessibilityAction = useCallback((evt) => {
        const { internalValue: v, minimumValue: min, range: r, upperBound: max, step: s, } = latest.current;
        // An increment below `step` would snap back to the current value.
        const increment = Math.max(accessibilityIncrement ?? (s > 0 ? s : r / 10), s);
        let direction;
        if (evt.nativeEvent.actionName === 'increment') {
            direction = 1;
        }
        else if (evt.nativeEvent.actionName === 'decrement') {
            direction = -1;
        }
        else {
            return;
        }
        const next = clamp(snapToStep(v + direction * increment, min, s), min, max);
        // Clamping can pull a value that is already above `upperBound` down, so
        // ignore any result that moves against the requested direction.
        if ((next - v) * direction <= 0) {
            return;
        }
        gesture.current.current = next;
        setInternalValue(next);
        latest.current.onSlidingStart?.(v);
        latest.current.onValueChange?.(next);
        latest.current.onSlidingComplete?.(next);
    }, [accessibilityIncrement]);
    const activeColor = minimumTrackTintColor ??
        (error ? colors.status.negative : colors.gradients.ultimate.colors[0]);
    return (<View testID={testID} style={[staticStyles.container, containerStyle, { width }]} accessible accessibilityRole="adjustable" accessibilityLabel={accessibilityLabel} accessibilityValue={{
            min: minimumValue,
            max: maximumValue,
            now: internalValue,
            text: accessibilityValueText,
        }} accessibilityActions={ACCESSIBILITY_ACTIONS} onAccessibilityAction={onAccessibilityAction} {...panResponder.panHandlers}>
      {/* Children ignore touches so locationX is always relative to the container. */}
      <View pointerEvents="none" style={[
            staticStyles.track,
            { backgroundColor: maximumTrackTintColor ?? colors.borders.divider },
            trackStyle,
        ]}>
        <View style={[
            staticStyles.minimumTrack,
            {
                width: thumbLeft + thumbWidth / 2,
                backgroundColor: activeColor,
            },
        ]}/>
      </View>
      <View pointerEvents="none" style={[staticStyles.thumbContainer, { left: thumbLeft }]}>
        {renderThumbComponent ? (renderThumbComponent()) : (<View style={[styles.thumb, error && styles.thumbError, thumbStyle]}/>)}
      </View>
    </View>);
};
const staticStyles = StyleSheet.create({
    container: {
        height: rem(40),
        justifyContent: 'center',
    },
    track: {
        height: rem(8),
        borderRadius: rem(22),
        overflow: 'hidden',
    },
    minimumTrack: {
        height: '100%',
    },
    thumbContainer: {
        position: 'absolute',
        top: 0,
        bottom: 0,
        justifyContent: 'center',
    },
});
const styleSet = createStyleSheets((colors) => ({
    thumb: {
        backgroundColor: colors.cards.onDark,
        width: rem(16),
        height: rem(16),
        borderRadius: rem(8),
        borderWidth: rem(2),
        borderColor: colors.gradients.ultimate.colors[0],
    },
    thumbError: {
        borderColor: colors.status.negative,
    },
}));
export default Slider;
